import {CLStore} from 'common/src/redux/store';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';

type PersonaMinimal = {
  id: number;
  name: string | null;
};

type BrandPersonasContext = {
  personas: PersonaMinimal[] | null;
  isLoaded: boolean;
};

function brandPersonasContext(store: CLStore): AgentContext<BrandPersonasContext> {
  function getter(): BrandPersonasContext {
    const brandPersonasData = store.getState().brandGuidelinesPage.brandPersonas.data;

    if (!brandPersonasData) {
      return {
        personas: null,
        isLoaded: false,
      };
    }

    // Return only id and name for each persona
    const personas: PersonaMinimal[] = brandPersonasData.map((persona) => ({
      id: persona.id,
      name: persona.name,
    }));

    return {
      personas,
      isLoaded: true,
    };
  }

  function formatter({
    prevContext,
    currentContext,
  }: {
    prevContext: BrandPersonasContext | null;
    currentContext: BrandPersonasContext;
  }): string | null {
    const {personas, isLoaded} = currentContext;

    // If not loaded yet
    if (!isLoaded) {
      return 'Brand personas: not loaded and shown to user yet';
    }

    // First time loading or no previous context
    if (!prevContext || !prevContext.isLoaded) {
      if (personas && personas.length > 0) {
        const personasList = personas.map((p) => `ID ${p.id}: ${p.name || 'Unnamed'}`).join(', ');

        return `Brand personas loaded: ${personas.length} persona${personas.length === 1 ? '' : 's'} available (${personasList})`;
      } else {
        return 'Brand personas loaded: No personas created yet';
      }
    }

    // Both contexts have loaded personas - detect changes
    const prevPersonas = prevContext.personas || [];
    const currentPersonas = personas || [];

    // Check for additions
    const addedPersonas = currentPersonas.filter(
      (current) => !prevPersonas.some((prev) => prev.id === current.id),
    );

    // Check for deletions
    const deletedPersonas = prevPersonas.filter(
      (prev) => !currentPersonas.some((current) => current.id === prev.id),
    );

    const changes: string[] = [];

    if (addedPersonas.length > 0) {
      const addedList = addedPersonas.map((p) => `ID ${p.id}: ${p.name || 'Unnamed'}`).join(', ');
      changes.push(`Added: ${addedList}`);
    }

    if (deletedPersonas.length > 0) {
      const deletedList = deletedPersonas
        .map((p) => `ID ${p.id}: ${p.name || 'Unnamed'}`)
        .join(', ');
      changes.push(`Deleted: ${deletedList}`);
    }

    if (changes.length > 0) {
      return `Brand personas changed - ${changes.join('. ')}`;
    }

    // No changes detected
    return null;
  }

  return {
    title: 'Brand Personas',
    getter,
    formatter,
  };
}

export default brandPersonasContext;
